import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Inquiry = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    student_grade: '',
    program_interest: '',
    goals: '',
    how_did_you_hear: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const message = `Student Grade: ${formData.student_grade}
Program of Interest: ${formData.program_interest}
How They Heard: ${formData.how_did_you_hear}

Goals & Context:
${formData.goals}`;

      await axios.post(`${API}/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        inquiry_type: 'consultation',
        message,
      });

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div data-testid="inquiry-success-page">
        <Navbar />
        <section className="min-h-screen flex items-center justify-center bg-background pt-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl text-center py-24">
            <div className="text-sm font-medium tracking-[0.2em] uppercase text-accent mb-6">
              Inquiry Received
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-semibold text-primary mb-8 tracking-tight leading-tight">
              Thank you. We have your inquiry.
            </h1>
            <div className="space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>
                Our founder reviews each submission personally. You can expect a response within five business days.
              </p>
              <p>
                If we determine there is potential alignment, we will invite you to a private consultation.
              </p>
            </div>
            <a
              href="/"
              className="inline-block mt-12 text-primary border-b border-accent pb-1 font-medium hover:text-accent transition-colors"
            >
              Return Home
            </a>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="inquiry-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="text-sm font-medium tracking-[0.2em] uppercase text-accent mb-6">
            Qualifying Inquiry
          </div>
          <h1 className="text-5xl md:text-6xl font-heading font-semibold tracking-tight leading-[1.05] mb-8">
            Request Consultation
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-primary-foreground/80 max-w-2xl">
            We review every inquiry personally. The information below helps us assess fit before scheduling a private conversation.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <form
            onSubmit={handleSubmit}
            data-testid="inquiry-form"
            className="bg-card border border-border p-8 md:p-12"
          >
            <div className="space-y-8">
              {/* Contact */}
              <div>
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Contact
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="name" className="text-foreground font-medium mb-2 block text-sm">
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="inquiry-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Parent or student"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-foreground font-medium mb-2 block text-sm">
                      Email Address
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      data-testid="inquiry-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-foreground font-medium mb-2 block text-sm">
                      Phone <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      data-testid="inquiry-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="(813) 555-0100"
                    />
                  </div>
                </div>
              </div>

              {/* Student Context */}
              <div className="pt-6 border-t border-border">
                <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-accent mb-6">
                  Student Context
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="student_grade" className="text-foreground font-medium mb-2 block text-sm">
                      Student Grade
                    </Label>
                    <Select
                      required
                      value={formData.student_grade}
                      onValueChange={(value) => setFormData({ ...formData, student_grade: value })}
                    >
                      <SelectTrigger
                        data-testid="inquiry-grade"
                        className="h-12 bg-background border-border focus:border-accent rounded-sm"
                      >
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="8th">8th Grade</SelectItem>
                        <SelectItem value="9th">9th Grade</SelectItem>
                        <SelectItem value="10th">10th Grade</SelectItem>
                        <SelectItem value="11th">11th Grade</SelectItem>
                        <SelectItem value="12th">12th Grade</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="program_interest" className="text-foreground font-medium mb-2 block text-sm">
                      Program of Interest
                    </Label>
                    <Select
                      required
                      value={formData.program_interest}
                      onValueChange={(value) => setFormData({ ...formData, program_interest: value })}
                    >
                      <SelectTrigger
                        data-testid="inquiry-program"
                        className="h-12 bg-background border-border focus:border-accent rounded-sm"
                      >
                        <SelectValue placeholder="Select program" />
                      </SelectTrigger>
                      <SelectContent className="bg-background">
                        <SelectItem value="core">Core Program</SelectItem>
                        <SelectItem value="signature">Signature Program</SelectItem>
                        <SelectItem value="undecided">Undecided / Discuss</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="goals" className="text-foreground font-medium mb-2 block text-sm">
                      Goals & Context
                    </Label>
                    <Textarea
                      id="goals"
                      name="goals"
                      data-testid="inquiry-goals"
                      required
                      value={formData.goals}
                      onChange={handleChange}
                      rows={6}
                      className="bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Briefly describe the student’s academic profile, target colleges, and what you are hoping to address."
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="how_did_you_hear" className="text-foreground font-medium mb-2 block text-sm">
                      How did you hear about Lantern Prep?
                    </Label>
                    <Input
                      id="how_did_you_hear"
                      name="how_did_you_hear"
                      data-testid="inquiry-source"
                      type="text"
                      value={formData.how_did_you_hear}
                      onChange={handleChange}
                      className="h-12 bg-background border-border focus:border-accent focus:ring-1 focus:ring-accent/20 rounded-sm"
                      placeholder="Referral, school counselor, online, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-6 border-t border-border">
                <button
                  type="submit"
                  data-testid="inquiry-submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-primary text-primary-foreground px-12 py-4 rounded-sm font-medium tracking-wide hover:bg-accent hover:text-accent-foreground transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
                <p className="text-xs text-muted-foreground mt-6 leading-relaxed">
                  Submissions are reviewed personally by our founder. Expect a response within five business days. Lantern Prep provides educational consulting and advocacy support. Nothing on this site constitutes legal advice or creates an attorney–client relationship.
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Inquiry;
